import { type Descendant, Editor, Transforms } from 'slate';
import type TableSelection from '../TableSelection';
import TableUtils from '../TableUtils';
import {
  getTableStructureFromElement,
  type CellCoordinates,
} from '../TableStructure';
import AhaEditor from '../../../AhaEditor';
import TablePlugin from '../TablePlugin';
import type {
  TableCellElement,
  TableElement,
  TableRowElement,
} from '../../../types/aha-editor';

export function spliceFragmentIntoTable(
  editor: Editor,
  tableSelection: TableSelection,
  fragment: Descendant[]
): boolean {
  if (!editor?.selection) return false;

  // If we are pasting content that only contains a single table cell, the assumption is the content
  // will be pasted as block elements into the current selection inside the table, rather than pasting
  // entire cells
  if (TableUtils.fragmentIsOnlySingleCell(fragment)) {
    return false;
  }

  const table = TableUtils.currentTable(editor);
  if (!table) return false;

  const tableStructure = getTableStructureFromElement(editor, table[0]);

  // Create an editor instance to work with the fragment
  const ahaEditor = AhaEditor.getForEditor(editor);

  const fragmentAhaEditor = new AhaEditor();
  if (ahaEditor) {
    ahaEditor.registeredPlugins.forEach((plugin) => {
      // We only need to register plugins that have a schema, to make sure undefined node types are not removed in normalization
      if ('schema' in plugin) {
        fragmentAhaEditor.register(plugin);
      }
    });
  } else {
    fragmentAhaEditor.register(TablePlugin);
  }
  fragmentAhaEditor.editor.children = fragment;
  const fragmentEditor = fragmentAhaEditor.editor;

  // Normalize here to fill in missing cells so that we end up with
  // a rectangular table
  Editor.normalize(fragmentEditor, { force: true });

  const fragmentTable = fragmentEditor.children[0] as TableElement;
  const fragmentTableStructure = getTableStructureFromElement(
    fragmentEditor,
    fragmentTable
  );
  const tableSize = {
    width: tableStructure.width,
    height: tableStructure.height,
  };
  const fragmentTableSize = {
    width: fragmentTableStructure.width,
    height: fragmentTableStructure.height,
  };

  let cellPosition: CellCoordinates | undefined;

  // If we have multiple cells selected, we want the top left cell.
  // Otherwise, we want cell at the cursor position.
  if (tableSelection.multipleCellsSelected) {
    cellPosition = tableSelection.tableRange?.startPosition;
  } else {
    const cell = TableUtils.currentCell(editor);
    if (cell) {
      cellPosition = tableStructure.pathToCoordinates(cell[1].slice(-2));
    }
  }

  if (!cellPosition) return false;

  // We may need to expand the table to fit the fragment. Work out the extra
  // rows and columns needed and add them after the final cell in the table.
  const extraRows = Math.max(
    0,
    fragmentTableSize.height - (tableSize.height - cellPosition.rowIndex)
  );
  const extraColumns = Math.max(
    0,
    fragmentTableSize.width - (tableSize.width - cellPosition.columnIndex)
  );

  // Get the final cell in the table
  const finalCell = TableUtils.parentCell(
    editor,
    Editor.end(editor, table[1]).path
  );
  if (!finalCell) return false;

  for (let x = 0; x < extraColumns; x++) {
    TableUtils.insertColumn(editor, finalCell[1]);
  }
  for (let y = 0; y < extraRows; y++) {
    TableUtils.insertRow(editor, finalCell[1]);
  }

  // Iterate over the fragment table, matching the size of each destination
  // cell, removing its current content and adding the content of the fragment
  // cell.
  fragmentTable.children.forEach((row: TableRowElement, rowIndex) => {
    row.children.forEach((cell: TableCellElement, columnIndex) => {
      const fragmentCellPosition = fragmentTableStructure.pathToCoordinates([
        rowIndex,
        columnIndex,
      ]);
      if (!fragmentCellPosition) return;

      const targetCellPosition = {
        rowIndex: cellPosition.rowIndex + fragmentCellPosition.rowIndex,
        columnIndex:
          cellPosition.columnIndex + fragmentCellPosition.columnIndex,
      };

      let targetCell = TableUtils.reloadCell(
        editor,
        table[1],
        targetCellPosition
      );
      if (!targetCell) return;

      TableUtils.matchSizeOfCell(editor, table, targetCellPosition, cell);

      targetCell = TableUtils.reloadCell(editor, table[1], targetCellPosition);
      if (!targetCell) return;

      Editor.withoutNormalizing(editor, () => {
        const path = [...table[1], ...targetCell.relativePath, 0];
        targetCell.tableCell.children.forEach(() => {
          Transforms.removeNodes(editor, {
            at: path,
          });
        });
        Transforms.insertNodes(editor, cell.children, {
          at: path,
        });

        const { type, children, ...props } = cell;

        Transforms.setNodes(editor, props, {
          at: [...table[1], ...targetCell.relativePath],
        });
      });
    });
  });

  // Set the selection to the top left cell of the newly pasted cells
  const topLeftCell = TableUtils.reloadCell(editor, table[1], cellPosition);
  if (topLeftCell) {
    Transforms.select(
      editor,
      Editor.start(editor, [...table[1], ...topLeftCell.relativePath])
    );
  }

  return true;
}
